import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
	type LWApiPublicProductFlatDetailsParamsRawI,
	type LWApiPublicProductFlatDetailsResultFlatOptionValueRawI,
	type LWApiPublicProductFlatDetailsResultRawI,
} from '@liquidweb-dev/club-sauce/public/product/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import api from 'modules/queries/api';

export const PRODUCT_FLAT_DETAILS_QUERY_KEY = '/product/flatDetails';

const useProductFlatDetails = <TData = LWApiPublicProductFlatDetailsResultRawI>(
	params: LWApiPublicProductFlatDetailsParamsRawI,
	options?: UseQueryOptions<
		LWApiPublicProductFlatDetailsResultRawI,
		ApiErrorI,
		TData
	>,
) =>
	useQuery({
		queryKey: [PRODUCT_FLAT_DETAILS_QUERY_KEY, params],
		queryFn: () => api.product.apiRaw.flatDetails(params),
		...options,
	});

export const createSslDvOptionValues = (
	data: LWApiPublicProductFlatDetailsResultRawI,
) => {
	const optionValues: Record<
		string,
		LWApiPublicProductFlatDetailsResultFlatOptionValueRawI
	> = {};

	if (!data?.flat_options) {
		return optionValues;
	}

	data.flat_options.forEach((option) => {
		option.values.forEach((value) => {
			optionValues[value.value] = value;
		});
	});

	return optionValues;
};

const selectSslDvFlatOptions = (
	data: LWApiPublicProductFlatDetailsResultRawI,
) => {
	const optionValues = createSslDvOptionValues(data);

	return { ...data, optionValues };
};

export const useSslDvFlatDetails = (
	options?: Omit<
		UseQueryOptions<LWApiPublicProductFlatDetailsResultRawI, ApiErrorI>,
		'select'
	>,
) =>
	useQuery({
		queryKey: [PRODUCT_FLAT_DETAILS_QUERY_KEY, { product_code: 'SSL.DV' }],
		queryFn: () => api.product.apiRaw.flatDetails({ product_code: 'SSL.DV' }),
		staleTime: Infinity,
		select: selectSslDvFlatOptions,
		...options,
	});

export default useProductFlatDetails;
